import { QueryHookOptions, gql } from '@apollo/client'
import { usePersistedCacheQuery } from 'app/hooks'
import { Chat } from 'app/types'

const query = gql`
  query getUserChats($page: Int!, $limit: Int!, $searchTerm: String) {
    searchChats(page: $page, limit: $limit, searchTerm: $searchTerm) {
      totalDocs
      page
      docs {
        id
        lastMessage {
          id
          message
          createdAt
          user {
            id
            fullName
            avatar {
              id
              sizes {
                small {
                  url
                }
                medium {
                  url
                }
              }
            }
          }
        }
        participants {
          id
          fullName
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
        }
      }
    }
  }
`

export interface UserChatsQueryInput {
  page: number
  limit: number
  sort: string
  searchTerm?: string
}

export interface UserChatsQueryOutput {
  searchChats: {
    totalDocs: number
    page: number
    docs: {
      id: string
      lastMessage: {
        id: string
        message: string
        createdAt: string
        user: {
          id: string
          fullName: string
          avatar: {
            id: string
            sizes: {
              small: {
                url: string
              }
              medium: {
                url: string
              }
            }
          }
        }
      }
      participants: {
        id: string
        fullName: string
        avatar: {
          id: string
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
      }[]
    }[]
  }
}

const defaultVariables: UserChatsQueryInput = {
  page: 1,
  limit: 10,
  sort: '-createdAt',
  searchTerm: '',
}

export const useUserChatsQuery = (
  options: QueryHookOptions<UserChatsQueryOutput, UserChatsQueryInput>
) => usePersistedCacheQuery<UserChatsQueryOutput, UserChatsQueryInput>(query, options)

const mapFn = (data: UserChatsQueryOutput['searchChats'] | undefined, userId: string): Chat[] => {
  if (!data || !data.docs) {
    return []
  }

  return data.docs
    .map((chat) => {
      const participant = chat.participants.find((p) => p.id !== userId)
      if (!participant) {
        return null
      }

      return {
        id: chat.id,
        lastMessage: chat.lastMessage
          ? {
              id: chat.lastMessage?.id,
              message: chat.lastMessage?.message,
              createdAt: chat.lastMessage?.createdAt,
              user: {
                id: chat.lastMessage?.user.id,
                fullName: chat.lastMessage?.user.fullName,
                avatar: {
                  small: chat.lastMessage?.user.avatar?.sizes.small.url,
                  medium: chat.lastMessage?.user.avatar?.sizes.medium.url,
                },
              },
            }
          : undefined,
        user: {
          id: participant.id,
          fullName: participant.fullName,
          avatar: {
            small: participant.avatar?.sizes.small.url,
            medium: participant.avatar?.sizes.medium.url,
          },
        },
      }
    })
    .filter(Boolean) as Chat[]
}

export const userChatsQuery = {
  query,
  mapFn,
  defaultVariables,
}
